import { IPromise, IQService } from 'angular';
import { ICartAddress } from '../tn.common/classes/Model/Cart';
import DjangoClient from '../tn.django/django-client.service/djangoClient';
import { UserService } from '../tn.user/service.tn.user';
import { TnError } from '../tn.common';

export type StripeBrand =
    | 'Visa'
    | 'American Express'
    | 'MasterCard'
    | 'Discover'
    | 'JCB'
    | 'Diners Club'
    | 'Unknown';

export class StripeNewCreditCard {
    public number = '';
    public isValid = null;
    public cardType = null;
    public expirationMonth = new Date().getMonth() + 1;
    public default = false;
    public expirationYear = new Date().getFullYear() - 2000;
    public CVC?: string = undefined;
}

export interface IStripeList<T> {
    object: 'list';
    data: T[];
    has_more: boolean;
    url: string;
}
export type StripeCheck = 'pass' | 'fail' | 'unavailable' | 'unchecked';
export type StripeFunding = 'credit' | 'debit' | 'prepaid' | 'unknown';
export type IStripeAddress = {
    city: string;
    // Two-letter country code (ISO 3166-1 alpha-2).
    country: string;
    // Address line 1 (e.g., street, PO Box, or company name).
    line1: string;
    // Address line 2 (e.g., apartment, suite, unit, or building).
    line2?: string;
    // ZIP or postal code.
    postal_code: string;
    // State, county, province, or region.
    state: string;
};

export interface IStripeDiscount {
    // https://stripe.com/docs/api/discounts/object
    id: string;
    object: string;
    checkout_session: string | null;
    coupon: {
        id: string;
        object: string;
        amount_off: number | null;
        created: number;
        currency: string;
        duration: string;
        duration_in_months: number | null;
        livemode: boolean;
        max_redemptions: number | null;
        metadata: Record<string, unknown>;
        name: string;
        percent_off: number | null;
        redeem_by: number | null;
        times_redeemed: number;
        valid: boolean;
    };
    customer: string;
    end: number | null;
    invoice: string | null;
    invoice_item: string | null;
    promotion_code: string | null;
    start: number;
    subscription: string | null;
}

export type StripeCustomerTaxIdType =
    | 'au_trn'
    | 'au_abn'
    | 'au_arn'
    | 'bg_uic'
    | 'br_cnpj'
    | 'br_cpf'
    | 'ca_bn'
    | 'ca_gst_hst'
    | 'ca_pst_bc'
    | 'ca_pst_mb'
    | 'ca_pst_sk'
    | 'ca_qst'
    | 'ch_vat'
    | 'cl_tin'
    | 'eg_tin'
    | 'es_cif'
    | 'eu_oss_vat'
    | 'eu_vat'
    | 'gb_vat'
    | 'ge_vat'
    | 'hk_br'
    | 'hu_tin'
    | 'id_npwp'
    | 'il_vat'
    | 'in_gst'
    | 'in_gst'
    | 'is_vat'
    | 'jp_cn'
    | 'jp_rn'
    | 'jp_trn'
    | 'ke_pin'
    | 'kr_brn'
    | 'li_uid'
    | 'mx_rfc'
    | 'my_frp'
    | 'my_itn'
    | 'my_sst'
    | 'no_vat'
    | 'nz_gst'
    | 'ph_tin'
    | 'ru_inn'
    | 'ru_kpp'
    | 'sa_vat'
    | 'sg_gst'
    | 'sg_uen'
    | 'si_tin'
    | 'th_vat'
    | 'tr_tin'
    | 'tw_vat'
    | 'ua_vat'
    | 'unknown'
    | 'us_ein'
    | 'za_vat';

export type StripeCustomerTaxIdVerificationStatus =
    | 'unavailable'
    | 'pending'
    | 'unverified'
    | 'verified';

export interface IStripeCustomerTaxId {
    // https://stripe.com/docs/api/customer_tax_ids
    id: string;
    object: 'tax_id';
    country: string;
    // ID of the customer
    customer: string;
    type: StripeCustomerTaxIdType;
    value: string;
    created: number;
    livemode: boolean;
    verification: {
        status: StripeCustomerTaxIdVerificationStatus;
        verified_address: string | null;
        verified_name: string | null;
    };
}

export interface IStripeCustomer {
    // https://stripe.com/docs/api/customers/object
    id: string;
    object: 'customer';
    address?: IStripeAddress | null;
    balance: number;
    // cards isn't documented at  https://stripe.com/docs/api/customers/object
    // but we get it back from our Django API. Not sure if this is from an old
    // stripe api, or if it's a custom field we added to the customer object.
    cards: IStripeList<StripeCard>;
    created: number;
    currency: string;
    // ID of the default payment source for the customer.
    default_source?: string | null;
    delinquent: boolean;
    description?: string | null;
    discount?: IStripeDiscount | null;
    email?: string | null;
    invoice_prefix: string;
    invoice_settings: {
        custom_fields: null;
        default_payment_method: string | null;
        footer: null;
        rendering_options: null;
    };
    livemode: boolean;
    metadata: Record<string, unknown>;
    name?: string;
    next_invoice_sequence: number;
    phone?: string | null;
    preferred_locales: string[];
    shipping?: {
        address: IStripeAddress;
        name?: string;
        phone?: string;
    };
    tax_exempt: boolean;
    tax_ids: IStripeList<IStripeCustomerTaxId>;
    account_balance: number;
}

export interface StripeCard {
    id: string;
    object: 'card';
    address_city: string;
    address_country: string;
    address_line1: string;
    // https://stripe.com/docs/api/tokens/object#token_object-card-address_line1_check
    address_line1_check: StripeCheck;
    address_line2: string;
    address_state: string;
    address_zip: string;
    address_zip_check: StripeCheck;
    brand: StripeBrand;
    // https://stripe.com/docs/api/tokens/object#token_object-card-country
    // two letter country ISO code
    country: string;
    customer: string;
    cvc_check: StripeCheck;
    dynamic_last4: string | null;
    exp_month: number;
    exp_year: number;
    fingerprint: string;
    funding: StripeFunding;
    last4: string;
    metadata: Record<string, string>;
    name: string;
    tokenization_method: null;
}

// TODO: I'm not sure who created this particular structure, but it
// should probably just be a string.
export interface IDefaultPaymentMethod {
    default: string;
}

// see: https://stripe.com/docs/stripe-js/v2
export class StripeService {
    static $inject = ['$q', 'djangoClient', 'tn.user.service', 'Stripe'];

    /*eslint camelcase: 0 */
    protected creditCards: StripeCard[] = [];
    protected defaultCard: IDefaultPaymentMethod = {
        default: '',
    };

    constructor(
        protected $q: IQService,
        protected djangoClient: DjangoClient,
        protected userService: UserService,
        protected Stripe: stripe.StripeStatic
    ) {
        this.Stripe.setPublishableKey('TN_STRIPEKEY');
        userService.user$.subscribe(() => {
            this.getCardsFromStripe();
        });
        userService.logout$.subscribe(() => {
            this.clearCards();
        });
    }

    // https://stripe.com/docs/stripe-js/v2#card-createToken
    public cardCreateToken(
        tokenData: stripe.StripeCardTokenData
    ): IPromise<stripe.StripeCardTokenResponse> {
        return this.$q((resolve, reject) => {
            this.Stripe.card.createToken(tokenData, (status, response) => {
                if (status !== 200) {
                    return reject(response);
                }
                resolve(response);
            });
        });
    }

    public createTokenForCard(
        cc: StripeNewCreditCard,
        address: ICartAddress
    ): IPromise<stripe.StripeCardTokenResponse> {
        // merge tn types to stripe type..
        // TODO: update tn types to align with stripe.
        const tokenData: stripe.StripeCardTokenData = {
            number: cc.number,
            exp_month: cc.expirationMonth,
            exp_year: cc.expirationYear,
            cvc: cc.CVC,
            name: address.name,
            address_line1: address.address1,
            address_line2: address.address2,
            address_city: address.city,
            address_state: address.state,
            address_zip: address.zipcode,
            address_country: address.country,
        };
        return this.cardCreateToken(tokenData);
    }

    // Save the stripe card to the user for future use. This request is proxied through API.
    public saveCard(
        response: stripe.StripeCardTokenResponse & { vat?: string | null },
        vat: string | null = null
    ): IPromise<StripeCard> {
        // We're sending the response from calling the `Stripe.card.createToken()` method
        // of the Stripe library directly to our server. That response does not
        // include a vat, description, metadata or any custom field
        // https://stripe.com/docs/stripe-js/v2#card-createToken
        // Therefore it is added manually, if it exist.

        if (vat) {
            // can this be moved to metadata?
            // see: https://stripe.com/docs/api/tokens/object#token_object-card-metadata
            response.vat = vat;
        }
        const user = this.userService.getUser();
        if (!this.userService.isLoggedIn() || !user) {
            throw new TnError('User not logged in', user);
        }
        return this.djangoClient.post<
            StripeCard,
            stripe.StripeCardTokenResponse
        >('/api/1/users/' + user.id + '/cards/', response);
    }

    // this returns the locally cached cards. You must call getCardsFromStripe
    // and wait for it to resolve before this will return a result.
    public getCards(): StripeCard[] {
        return this.creditCards;
    }

    public clearCards(): void {
        // Clear out old cards so we don't get stale info or duplicates
        this.creditCards.splice(0, this.creditCards.length);
    }

    public getDefaultCard(): IDefaultPaymentMethod | null {
        return this.defaultCard;
    }

    public setDefaultCard(payment_method_id: string): IPromise<void> {
        const user = this.userService.getUser();
        if (!this.userService.isLoggedIn() || !user) {
            throw new TnError('User not logged in', user);
        }
        return this.djangoClient
            .put(
                '/api/1/users/' +
                    user.id +
                    '/cards/' +
                    payment_method_id +
                    '/default/'
            )
            .then(() => {
                this.defaultCard['default'] = payment_method_id;
            });
    }

    public getCardById(cardId: string): StripeCard | undefined {
        return this.creditCards.find((card) => card.id === cardId);
    }

    public containsCardWithHandle(payment_method_id: string): boolean {
        return (
            this.creditCards.filter((c) => c.id === payment_method_id).length >
            0
        );
    }

    public isCardExpired(card: StripeCard): boolean {
        const now = new Date();
        const currentMonth = now.getMonth() + 1;
        const currentYear = now.getFullYear();
        return (
            card.exp_year < currentYear ||
            (card.exp_year === currentYear && card.exp_month < currentMonth)
        );
    }

    public getCardsFromStripe(): IPromise<StripeCard[]> {
        const user = this.userService.user$.value;
        if (!this.userService.isLoggedIn() || !user) {
            return this.$q.when([]);
        }

        return this.djangoClient
            .get<IStripeCustomer>('/api/1/users/' + user.id + '/cards/')
            .then((response) => {
                // console.log('getCardsFromStripe.IDjangoCardsResponse', response);
                this.clearCards();
                this.defaultCard['default'] = response.default_source || '';
                response.cards.data.forEach((card) => {
                    this.creditCards.push(card);
                });

                return this.creditCards;
            });
    }

    public validateCardNumber(cardNumber: string): boolean {
        return this.Stripe.validateCardNumber(cardNumber);
    }
    public validateExpiry(month: string, year: string): boolean {
        return this.Stripe.validateExpiry(month, year);
    }
    public validateCVC(cvc: string): boolean {
        return this.Stripe.validateCVC(cvc);
    }
    public cardType(cardNumber: string): StripeBrand {
        return this.Stripe.cardType(cardNumber);
    }

    public deleteCardFromStripe(card: StripeCard): IPromise<void> {
        const user = this.userService.user$.value;
        if (!this.userService.isLoggedIn() || !user) {
            throw new TnError('User not logged in', user);
        }
        return this.djangoClient
            .del('/api/1/users/' + user.id + '/cards/' + card.id + '/')
            .then(() => {
                this.creditCards.splice(this.creditCards.indexOf(card), 1);
            });
    }
}
